import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, TextField, Avatar, ToggleButton, ToggleButtonGroup, Modal, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DataTable from 'react-data-table-component';
import { Add } from '@mui/icons-material';
import { primaryColor } from '../theme';
import axios from 'axios';
import moment from 'moment';

const roles = ['Head Office Admin', 'Regional Manager', 'Content Editor', 'Content Reviewer'];
const locations = ['Location 1', 'Location 2', 'Location 3'];

const UserManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filterRole, setFilterRole] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    role: '',
    location: '',
    email: '',
    contact: ''
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userId = JSON.parse(localStorage.getItem('user')).id;
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users`, {
          params: { userId }
        });
        setUsers(response.data);
        const filterRole = [...new Set(response.data.map(item => item.role))];
        setUniqueRoles(filterRole);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  const handleRoleChange = (event, newRoles) => {
    setFilterRole(newRoles);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenDetails = (user) => {
    setSelectedUser(user);
    setOpenDetails(true);
  };

  const columns = [
    { name: 'Profile Picture', selector: row => <Avatar src={row.profilePicture} />, width: '80px' },
    { name: 'Name', selector: row => `${row.firstName} ${row.lastName}`, sortable: true },
    { name: 'Contact', selector: row => row.contact, sortable: true },
    { name: 'Status', selector: row => row.status, sortable: true },
    { name: 'Role', selector: row => row.role, sortable: true },
    { name: 'Date Registered', selector: row => moment(row.dateCreated).format('MMMM Do YYYY, h:mm a'), sortable: true },
    { name: 'Last Active Date', selector: row => row.lastLogin ? moment(row.lastLogin).format('MMMM Do YYYY, h:mm a') : 'Never logged in', sortable: true },
  ];

  const handleCloseDetails = () => setOpenDetails(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleRowClick = (row) => {
    navigate(`/user/${row.id}/details`);
  };

  const handleStatusChange = () => {
    // Logic to change user status
    const updatedUser = { ...selectedUser, status: selectedUser.status === 'Active' ? 'Inactive' : 'Active' };
    setSelectedUser(updatedUser);
    // Update userData with the new status
    const updatedUserData = users.map(user => user.id === updatedUser.id ? updatedUser : user);
    // Update the state with the new user data
    setUsers(updatedUserData);
  };

  const filteredData = users.filter(user => 
    (filterRole.length === 0 || filterRole.includes(user.role)) &&
    (searchText === user.firstName?.toLowerCase().includes(searchText.toLowerCase()) || user.lastName?.toLowerCase().includes(searchText.toLowerCase()) || user.contact?.toLowerCase().includes(searchText.toLowerCase()) || user.email?.toLowerCase().includes(searchText.toLowerCase()) || user.role?.toLowerCase().includes(searchText.toLowerCase()))
  );

  return (
    <Container maxWidth="false" sx={{ mt: 4, display: 'flex' }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '20%', mr: 4 }}>
        <Typography variant="h6" gutterBottom style={{ color: primaryColor }}>
          Filter By
        </Typography>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Role
          </Typography>
          <ToggleButtonGroup
            value={filterRole}
            onChange={handleRoleChange}
            aria-label="role"
            orientation="vertical"
            fullWidth
          >
            {uniqueRoles.map(role => (
              <ToggleButton key={role} value={role} aria-label={role} sx={{ borderColor: primaryColor, color: primaryColor }}>
                {role}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Search"
            variant="outlined"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: '70%' }}

          />
          <Button variant="contained" color="primary" startIcon={<Add />} sx={{ backgroundColor: primaryColor }} onClick={() => navigate('/users/bulk-add-users')}>
            Add Multiple Users
          </Button>
          <Button variant="contained" color="primary" startIcon={<Add />} sx={{ backgroundColor: primaryColor }} onClick={() => navigate('/users/add-user')}>
            Add New User
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          onRowClicked={handleRowClick}
          highlightOnHover
          pointerOnHover
          customStyles={{
            headCells: {
              style: {
                backgroundColor: primaryColor,
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
              },
            },
            cells: {
              style: {
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              },
            },
          }}
        />
      </Box>
    </Container>
  );
};

export default UserManagement;